import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublishValidationSettings = ({ sessionToken }) => {
  const [settings, setSettings] = useState({
    validation_enabled: true,
    min_feedback_required: 3,
    min_rating_required: 4.0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log('Fetching settings from:', `${API}/admin/publish-validation-settings`);
      console.log('Using session token:', sessionToken ? 'Present' : 'Missing');
      
      const response = await axios.get(`${API}/admin/publish-validation-settings`, {
        headers: { 
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Settings response:', response.data);
      setSettings(response.data);
      setError('');
    } catch (err) {
      console.error('Settings fetch error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      
      let errorMsg;
      if (err.response?.status === 404) {
        errorMsg = 'Settings endpoint not found';
      } else if (err.response?.status === 401) {
        errorMsg = 'Authentication issue: Please ensure you are logged in as an admin user (arun_shankar222@yahoo.com) in the main app first, then access admin panel';
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied: Your account needs admin privileges. Please contact system administrator.';
      } else {
        errorMsg = `Failed to load settings (${err.response?.status || 'Network Error'})`;
      }
        
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setMessage('');
      setError('');

      const response = await axios.post(`${API}/admin/publish-validation-settings`, settings, {
        headers: { 
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        }
      });

      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError('Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        📝 Publish Validation Settings
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {message && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {message}
        </div>
      )}

      <div className="space-y-6">
        {/* Enable/Disable Validation */}
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Enable Publish Validation</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Require minimum feedback and ratings before allowing campaign publishing
            </p>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => handleInputChange('validation_enabled', !settings.validation_enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                settings.validation_enabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.validation_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Minimum Feedback Required */}
        <div>
          <label className="block font-medium text-gray-900 dark:text-white mb-2">
            Minimum Feedback Required
          </label>
          <input
            type="number"
            value={settings.min_feedback_required}
            onChange={(e) => handleInputChange('min_feedback_required', parseInt(e.target.value) || 0)}
            disabled={!settings.validation_enabled}
            className={`w-full px-3 py-2 border rounded-md ${
              settings.validation_enabled 
                ? 'border-gray-300 dark:border-gray-600' 
                : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700'
            } dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
            min="0"
            max="100"
          />
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Number of user feedback entries required before publishing
          </p>
        </div>

        {/* Minimum Rating Required */}
        <div>
          <label className="block font-medium text-gray-900 dark:text-white mb-2">
            Minimum Average Rating Required
          </label>
          <input
            type="number"
            value={settings.min_rating_required}
            onChange={(e) => handleInputChange('min_rating_required', parseFloat(e.target.value) || 0)}
            disabled={!settings.validation_enabled}
            step="0.1"
            min="0"
            max="5"
            className={`w-full px-3 py-2 border rounded-md ${
              settings.validation_enabled 
                ? 'border-gray-300 dark:border-gray-600' 
                : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700'
            } dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
          />
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Minimum average star rating required (0.0 - 5.0)
          </p>
        </div>

        {/* Current Status */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
          <h5 className="font-medium text-gray-900 dark:text-white mb-2">Current Status</h5>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {settings.validation_enabled ? (
              <>
                <p className="text-yellow-600 dark:text-yellow-400">
                  ⚠️ Validation is <strong>ENABLED</strong>
                </p>
                <p>
                  Campaigns need at least <strong>{settings.min_feedback_required}</strong> feedback entries
                  with an average rating of <strong>{settings.min_rating_required}+</strong> stars to publish.
                </p>
              </>
            ) : (
              <>
                <p className="text-green-600 dark:text-green-400">
                  ✅ Validation is <strong>DISABLED</strong>
                </p>
                <p>
                  All campaigns can be published immediately without feedback requirements.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className={`px-4 py-2 rounded-md text-white font-medium ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
            } transition-colors`}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishValidationSettings;